import React, { CSSProperties, useRef, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import {
  clampText,
  colors2,
  UI_CAPTION_DF,
  UI_CAPTION_SEMIBOLD_DF,
  getColorContrast,
} from "oolib";
import TooltipV2 from "@/components/UI/Tooltip";
import { TooltipContent } from "../CustomTooltip";
import { useTrackMousePosition } from "../utils/useTrackMousePosition";
import { useAxisMeasure } from "./utils/useAxisMeasure";
import { useFader } from "./utils/useFader";

import { usePrepareHeatMapData } from "./utils/usePrepareHeatMapData";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";
import { mapValueToColor } from "./utils/mapValueToColor";
import { VizArea, VizShell } from "../common/VizShell";
import { useCustomScrollbar } from "./utils/useCustomScrollbar";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SidePanel } from "@/components/UI/SidePanel";
import { cellKey, findSelectedCell, isCellClickable } from "./utils/selectedCell";

const StyledGridContainer = styled.div`
  ${globalScrollbarStyling}
`;

const StyledGridArea = styled.div<{
  showFader?: boolean;
  showHorizontalFader?: boolean;
}>`
  /* Hide native scrollbars - we'll use custom ones */
  &::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    background: transparent;
  }

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
`;

// Custom scrollbar components - styled to match OverlayScrollbar
const CustomScrollbarTrack = styled.div<{
  orientation: "vertical" | "horizontal";
  visible: boolean;
}>`
  position: absolute;
  ${(props) =>
    props.orientation === "horizontal"
      ? `
    left: 0;
    right: 0;
    bottom: 0;
    height: 1rem;
  `
      : `
    right: 0;
    top: 0;
    bottom: 0;
    width: 1rem;
  `}
  background: transparent;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.15s ease;
  user-select: none;
  z-index: 1000;
  pointer-events: ${(props) => (props.visible ? "auto" : "none")};
  cursor: pointer;
`;

const CustomScrollbarThumb = styled.div<{
  isDragging?: boolean;
  orientation: "vertical" | "horizontal";
}>`
  position: absolute;
  ${(props) =>
    props.orientation === "horizontal"
      ? `
    top: 2px;
    bottom: 2px;
    min-width: 20px;
  `
      : `
    left: 2px;
    right: 2px;
    min-height: 20px;
  `}
  background: ${colors2.grey15};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${colors2.grey20};
  }

  ${(props) =>
    props.isDragging &&
    `
    background: ${colors2.grey30} !important;
  `}
`;

export interface HeatMapGridProps {
  data: any;
  valuePath: string | string[];
  tooltipLabelsMapping: { [key: string]: string }; //use if you have the luxury to define labels from outside the data object
  tooltipLabelsPath: string | string[]; //TAKES PRESIDENCE OVER THE ABOVE, IF EXISTS.use if labels can be found only inside the data object. Columns are keyed by the sibling `value` of each entry when the data carries one (same-named columns stay distinct, ordered by total count) — see usePrepareHeatMapData
  labelPath: string;
  onClick?: (name: string) => void;
  // Body / sub-header of the cell detail panel. Cells are only clickable when
  // renderCellDetails is given: the grid knows nothing about what a cell
  // contains, so a consumer that can't answer that gets no panel.
  renderCellDetails?: (cell: any) => React.ReactNode;
  renderCellSubHeader?: (cell: any) => React.ReactNode;
  valueSuffix?: string;
  showCount?: boolean;
  showPercent?: boolean;
  breakdownDisplayType?: "grouped" | "stacked";
  colorIdx?: number; //defines the starting point for the colors in the dataVizColors array. Useful if you are rendering multiple such charts on one page, and you want some variation in color
  summarizeAfterIdx?: number;
  style?: CSSProperties;
  xAxisLabel?: string; // Label for the X-axis (horizontal axis)
  yAxisLabel?: string; // Label for the Y-axis (vertical axis)
  height?: number;
  xAxisLabelPadding?: number; // Top padding for x-axis labels (column headers) in pixels
  yAxisLabelPadding?: number; // Left padding for y-axis labels (row headers) in pixels
  plusMinusMode?: boolean; // Cells can be negative: diverge the colour scale from zero and sign the numbers (default: false)
  hatchDiagonal?: boolean; // Same field on both axes: cells whose row id equals their column id render hatched, no value
}

const _configs = {
  labelsToGridGap: 4,
  gridGap: 2,
  // Room for the first row's and column's ring, which would otherwise be
  // clipped by the scroll container.
  gridEdgeInset: 2,
  cellRoundness: 4,
  cellSize: 48,
  axisLabelWidth: 150,
  headerMinWidth: 150,
  XYlabelSize: 24,
  xAxisLabelPadding: 10, // Top padding for x-axis labels (column headers)
  yAxisLabelPadding: 10, // Left padding for y-axis labels (row headers)
};

// "+4" / "−4" / "0" — in plus-minus mode the sign is half the reading, so it
// is always printed, with a true minus rather than a hyphen.
const signed = (n) => (n > 0 ? `+${n}` : n < 0 ? `\u2212${Math.abs(n)}` : "0");

// 1px clear of the cell, drawn as a pseudo element so it never affects layout.
// Its outer edge lands on the grid gap, so it can't bleed into a neighbour.
const StyledHeatMapCell = styled.div<{ $isSelected?: boolean; $isClickable?: boolean }>`
  position: relative;
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  display: flex;
  justify-content: center;
  align-items: center;

  &::after {
    content: "";
    position: absolute;
    inset: -${_configs.gridGap}px;
    border: 2px solid transparent;
    border-radius: ${_configs.cellRoundness + _configs.gridGap}px;
    pointer-events: none;
  }

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      &:hover::after {
        border-color: ${colors2.grey40};
      }
    `}

  /* Selected = this cell's panel is open, so the ring outlives the hover. */
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      &::after,
      &:hover::after {
        border-color: ${colors2.grey90};
      }
    `}
`;

// Hatched placeholder for a cell that pairs a value with itself.
const HatchedCell = () => (
  <div style={{ padding: `${_configs.gridGap / 2}px`, margin: `-${_configs.gridGap / 2}px` }}>
    <div
      style={{
        height: _configs.cellSize + "px",
        width: _configs.cellSize + "px",
        boxSizing: "border-box",
        border: `1px solid ${colors2.grey15}`,
        borderRadius: _configs.cellRoundness + "px",
        // Thin "/" stripes drawn as an 8px square tile (one band through the
        // centre, half-bands at the two corners). Tiling on whole pixels keeps
        // every stripe at the same sub-pixel offset, so anti-aliasing renders
        // them all alike; a repeating-gradient with a fractional period did not.
        backgroundImage: `linear-gradient(-45deg,
          ${colors2.grey15} 0 0.5px,
          transparent 0.5px calc(50% - 0.5px),
          ${colors2.grey15} calc(50% - 0.5px) calc(50% + 0.5px),
          transparent calc(50% + 0.5px) calc(100% - 0.5px),
          ${colors2.grey15} calc(100% - 0.5px))`,
        backgroundSize: "8px 8px",
        // centre the tiling so the pattern runs out the same way at both corners
        backgroundPosition: "center",
        // the border is translucent; stop the stripes at the padding edge so
        // they don't show through it
        backgroundClip: "padding-box",
      }}
    />
  </div>
);

const HeatMapCell = ({
  value,
  dataMaxValue,
  dataMinValue = 0,
  plusMinusMode = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  isSelected,
}) => {
  const cellColor = mapValueToColor(
    value.labels.count,
    dataMinValue,
    dataMaxValue,
    plusMinusMode
  );

  // Determine text color based on contrast ratio with cell background
  const contrastWithBlack = getColorContrast(cellColor, colors2.black);
  const contrastWithWhite = getColorContrast(cellColor, colors2.white);
  const textColor =
    contrastWithBlack > contrastWithWhite ? colors2.black : colors2.white;

  return (
    <div
      onMouseEnter={() => onMouseEnter(cellColor)}
      onMouseLeave={onMouseLeave}
      style={{
        padding: `${_configs.gridGap / 2}px`,
        margin: `-${_configs.gridGap / 2}px`,
      }}
    >
      <StyledHeatMapCell
        onClick={onClick}
        $isClickable={!!onClick}
        $isSelected={isSelected}
        style={{
          height: _configs.cellSize + "px",
          width: _configs.cellSize + "px",
          background: cellColor,
          borderRadius: _configs.cellRoundness + "px",
        }}
      >
        <UI_CAPTION_SEMIBOLD_DF style={{ color: textColor }}>
          {plusMinusMode ? signed(value.labels.count) : `${value.labels.count}`}
        </UI_CAPTION_SEMIBOLD_DF>
      </StyledHeatMapCell>
    </div>
  );
};

const STYLED_UI_CAPTION_SEMIBOLD_DF = styled(UI_CAPTION_SEMIBOLD_DF)`
  ${clampText(2)}
`;

const HiddenScrollContainer = styled.div`
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Webkit browsers */
  }
`;

export const HeatMapGrid: React.FC<HeatMapGridProps> = ({
  data: _data,
  valuePath,
  tooltipLabelsMapping,
  tooltipLabelsPath,
  labelPath = "name",
  showPercent = true,
  colorIdx = 0,
  style = {},
  xAxisLabel,
  yAxisLabel,
  height = 500,
  showCount = true,
  onClick,
  renderCellDetails,
  renderCellSubHeader,
  summarizeAfterIdx = 5,
  xAxisLabelPadding = _configs.xAxisLabelPadding,
  yAxisLabelPadding = _configs.yAxisLabelPadding,
  plusMinusMode = false,
  hatchDiagonal = false,
}) => {
  const { tableData, columnHeaders, dataMaxValue } = usePrepareHeatMapData({
    _data,
    valuePath,
    tooltipLabelsMapping,
    tooltipLabelsPath,
    labelPath,
    showPercent,
    colorIdx,
    hatchDiagonal,
  });

  const heightForHeatMap = height - _configs?.XYlabelSize - 32; // padding of shell 16 * 2

  const scrollableRef = useRef(null);
  const { setFader, showFader, showHorizontalFader } = useFader(scrollableRef);

  const { xWidth, yWidth, setHorizontalRef, setVerticalRef } = useAxisMeasure(
    _configs.axisLabelWidth
  );

  // Custom scrollbar logic - extracted to a custom hook
  const {
    verticalScrollState,
    horizontalScrollState,
    isDraggingVertical,
    isDraggingHorizontal,
    visibility: {
      hasVerticalOverflow,
      hasHorizontalOverflow,
      verticalScrollbarVisible,
      horizontalScrollbarVisible,
    },
    handlers: {
      handleVerticalThumbMouseDown,
      handleHorizontalThumbMouseDown,
      handleVerticalTrackClick,
      handleHorizontalTrackClick,
      handleScrollbarMouseEnter,
      handleScrollbarMouseLeave,
      updateScrollbarPositions,
    },
  } = useCustomScrollbar(scrollableRef);

  // Rows are a fixed pitch: cell height plus the gap that follows it. Virtualizing
  // against the main scroll area keeps the row headers (which mirror its scrollTop)
  // in step with the grid without a second source of truth.
  const rowPitch = _configs.cellSize + _configs.gridGap;

  const rowVirtualizer = useVirtualizer({
    count: tableData.length,
    getScrollElement: () => scrollableRef.current,
    estimateSize: () => rowPitch,
    overscan: 6,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: columnHeaders.length,
    getScrollElement: () => scrollableRef.current,
    estimateSize: () => rowPitch,
    overscan: 4,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRowHeight = rowVirtualizer.getTotalSize();
  const virtualColumns = columnVirtualizer.getVirtualItems();
  const totalColumnWidth = columnVirtualizer.getTotalSize();

  // Update scrollbar positions on mount and when content changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateScrollbarPositions();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [tableData, updateScrollbarPositions]);

  // Grid-level mouse tracking for tooltip
  const gridRef = useRef<HTMLDivElement>(null);
  const { mousePosition, isHovering } = useTrackMousePosition({ containerRef: gridRef });
  const [hoveredCellData, setHoveredCellData] = useState<any>(null);
  // Only the key is stored; the labels are read from the current data, so the
  // panel never shows a cell that has since moved, emptied or gone.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selectedCell = useMemo(
    () => findSelectedCell(tableData, selectedKey),
    [tableData, selectedKey]
  );
  useEffect(() => {
    if (selectedKey && !selectedCell) setSelectedKey(null);
  }, [selectedKey, selectedCell]);

  return (
    <VizShell style={{ height: "100%" }}>
      <VizArea style={{ paddingTop: '0', ...style }}>
        <div style={{ height: "inherit" }}>

          {xAxisLabel && (
            <div
              style={{
                height: _configs?.XYlabelSize + "px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: colors2.grey5,
                color: colors2.grey80,
                marginLeft: yAxisLabel ? "2.4rem" : "0px",
                borderRadius: "4px",
                flexShrink: 0,
                overflow: "hidden",
                padding: "0 8px",
              }}
            >
              <UI_CAPTION_DF
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={xAxisLabel}
              >
                {xAxisLabel}
              </UI_CAPTION_DF>
            </div>
          )}
          {/* Main content row - Y-axis label + heatmap */}
          <div style={{ display: "flex", flexGrow: 1 }}>
            {/* Y-axis label - to the left of the heatmap */}
            {yAxisLabel && (
              <div
                className="heatMapYAxisLabelContainer"
                style={{
                  width: _configs?.XYlabelSize + "px",
                  alignSelf: "stretch",
                  maxHeight: heightForHeatMap + "px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: colors2.grey5,
                  borderRadius: "4px",
                  color: colors2.grey80,
                  flexShrink: 0,
                  overflow: "hidden",
                  padding: "8px 0",
                }}
              >
                <div
                  className="heatMapYAxisLabel"
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    whiteSpace: "nowrap",
                    display: "block",
                    maxHeight: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={yAxisLabel}
                >
                  <UI_CAPTION_DF
                    style={{
                      display: "inline",
                    }}
                  >
                    {yAxisLabel}
                  </UI_CAPTION_DF>
                </div>
              </div>
            )}

            {/* Main heatmap grid - back to original 2x2 structure */}
            <StyledGridContainer
              className="heatMapGridContainer"
              style={{
                display: "grid",
                gridTemplateColumns: `${yWidth}px 1fr`,
                gridTemplateRows: `${_configs.headerMinWidth}px 1fr`,
                gap: _configs.labelsToGridGap + "px",
                position: "relative",
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                maxHeight: heightForHeatMap + "px",
                paddingTop: xAxisLabelPadding + "px",
                paddingLeft: yAxisLabelPadding + "px",
              }}
              onMouseEnter={handleScrollbarMouseEnter}
              onMouseLeave={handleScrollbarMouseLeave}
            >
              {/* Top-left: Empty spacer */}
              <div style={{ gridRow: 1, gridColumn: 1 }} />

              {/* Top-right: Column headers (horizontal scroll only) */}
              <HiddenScrollContainer
                className="heatMapScrollContainerHorizontal"
                style={{
                  gridRow: 1,
                  gridColumn: 2,
                  overflowX: "auto",
                  overflowY: "hidden",
                  paddingLeft: _configs.gridEdgeInset + "px",
                }}
                onScroll={(e) => {
                  // Sync horizontal scroll with main content area
                  if (scrollableRef.current) {
                    scrollableRef.current.scrollLeft = (
                      e.target as HTMLElement
                    ).scrollLeft;
                  }
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: totalColumnWidth + "px",
                    minHeight: _configs.headerMinWidth + "px",
                    flexShrink: 0,
                  }}
                >
                  {virtualColumns.map((virtualColumn) => {
                    const header = columnHeaders[virtualColumn.index];
                    const index = virtualColumn.index;
                    if (!header) return null;
                    return (
                    <div
                      key={`label-${header.key}-${index}`}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        transform: `translateX(${virtualColumn.start}px)`,
                        width: _configs.cellSize + "px",
                        minHeight: _configs.headerMinWidth + "px",
                        display: "flex",
                        alignItems: "flex-end",
                        textWrap: "nowrap",
                        justifyContent: "center",
                        overflow: "visible",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          minWidth: xWidth + "px",
                          position: "absolute",
                          bottom: 0,
                          left: "50%",
                          transformOrigin: "left bottom",
                          transform: "rotate(-90deg) translateY(50%)",
                          display: "flex",
                        }}
                      >
                        <div ref={(el) => setHorizontalRef(index, el)}>
                          <STYLED_UI_CAPTION_SEMIBOLD_DF
                            style={{
                              whiteSpace: "normal",
                              lineHeight: "13px",
                            }}
                          >
                            {header.displayLabel}
                          </STYLED_UI_CAPTION_SEMIBOLD_DF>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </HiddenScrollContainer>

              {/* Bottom-left: Row headers (vertical scroll only) */}
              <HiddenScrollContainer
                className="heatMapScrollContainerVertical"
                style={{
                  gridRow: 2,
                  gridColumn: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingTop: _configs.gridEdgeInset + "px",
                }}
                onScroll={(e) => {
                  // Sync vertical scroll with main content area
                  if (scrollableRef.current) {
                    scrollableRef.current.scrollTop = (
                      e.target as HTMLElement
                    ).scrollTop;
                  }
                }}
              >
                <div
                  style={{
                    position: "relative",
                    height: totalRowHeight + "px",
                    minWidth: _configs.headerMinWidth + "px",
                    flexShrink: 0,
                  }}
                >
                  {virtualRows.map((virtualRow) => {
                    const row = _data[virtualRow.index];
                    if (!row) return null;
                    return (
                      <div
                        key={`name-${row[labelPath]}-${virtualRow.index}`}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          transform: `translateY(${virtualRow.start}px)`,
                          height: _configs.cellSize + "px",
                          minWidth: _configs.headerMinWidth + "px",
                          textAlign: "right",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          flexShrink: 0,
                        }}
                      >
                        <div ref={(el) => setVerticalRef(virtualRow.index, el)}>
                          <STYLED_UI_CAPTION_SEMIBOLD_DF
                            style={{ minWidth: _configs.headerMinWidth + "px" }}
                          >
                            {row[labelPath]}
                          </STYLED_UI_CAPTION_SEMIBOLD_DF>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </HiddenScrollContainer>

              {/* Bottom-right: Main content area (both scrolls) */}
              <StyledGridArea
                className="heatMapGridArea"
                showFader={showFader}
                showHorizontalFader={showHorizontalFader}
                style={{
                  gridRow: 2,
                  gridColumn: 2,
                  overflow: "auto",
                  position: "relative",
                  paddingTop: _configs.gridEdgeInset + "px",
                  paddingLeft: _configs.gridEdgeInset + "px",
                }}
                ref={scrollableRef}
                onScroll={(e) => {
                  // Sync scrolling with headers
                  const topHeader = (e.target as HTMLElement).parentElement
                    .children[1]; // Top-right header
                  const leftHeader = (e.target as HTMLElement).parentElement
                    .children[2]; // Bottom-left header

                  if (topHeader)
                    topHeader.scrollLeft = (e.target as HTMLElement).scrollLeft;
                  if (leftHeader)
                    leftHeader.scrollTop = (e.target as HTMLElement).scrollTop;

                  // Update fader visibility on scroll
                  setFader();

                  // Update custom scrollbar positions
                  updateScrollbarPositions();
                }}
              >
                <div
                  ref={gridRef}
                  style={{
                    position: "relative",
                    height: totalRowHeight + "px",
                    width: totalColumnWidth + "px",
                  }}
                >
                  {virtualRows.map((virtualRow) => {
                    const rowCells = tableData[virtualRow.index];
                    if (!rowCells) return null;
                    return (
                      <div
                        key={virtualRow.key}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          transform: `translateY(${virtualRow.start}px)`,
                          height: _configs.cellSize + "px",
                          width: totalColumnWidth + "px",
                        }}
                      >
                        {virtualColumns.map((virtualColumn) => {
                          const cellData = rowCells[virtualColumn.index];
                          if (!cellData) return null;
                          const isDiagonal =
                            hatchDiagonal &&
                            cellData.labels.rowValue !== undefined &&
                            cellData.labels.rowValue === columnHeaders[virtualColumn.index]?.key;
                          return (
                            <div
                              key={`${virtualRow.index}-${virtualColumn.index}`}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                transform: `translateX(${virtualColumn.start}px)`,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {isDiagonal ? (
                                <HatchedCell />
                              ) : (
                                <HeatMapCell
                                  dataMaxValue={dataMaxValue}
                                  plusMinusMode={plusMinusMode}
                                  value={cellData}
                                  onMouseEnter={(cellColor: string) => setHoveredCellData({ ...cellData.labels, barColor: cellColor })}
                                  onMouseLeave={() => setHoveredCellData(null)}
                                  isSelected={
                                    !!selectedCell &&
                                    selectedKey ===
                                      cellKey(cellData.labels, virtualRow.index, virtualColumn.index)
                                  }
                                  onClick={
                                    renderCellDetails && isCellClickable(cellData.labels)
                                      ? () =>
                                          setSelectedKey(
                                            cellKey(cellData.labels, virtualRow.index, virtualColumn.index)
                                          )
                                      : undefined
                                  }
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                {/* Single tooltip at grid level */}
                {isHovering && hoveredCellData && (
                  <TooltipV2
                    controlled
                    isOpen={true}
                    mousePosition={mousePosition}
                    primaryContent={{
                      layout: 'custom',
                      render: <TooltipContent type="heatmap" data={hoveredCellData} />,
                    }}
                  />
                )}
              </StyledGridArea>
              {showFader && (
                <div
                  data-hide-on-capture
                  style={{
                    background: `linear-gradient(to top, ${colors2.white}, hsla(0, 0%, 100%, 0))`,
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "40px",
                    pointerEvents: "none", // Allow scrollbar interaction
                    zIndex: 1,
                  }}
                />
              )}
              {showHorizontalFader && (
                <div
                  data-hide-on-capture
                  style={{
                    background: `linear-gradient(to left, ${colors2.white}, hsla(0, 0%, 100%, 0))`,
                    position: "absolute",
                    top: 0,
                    right: 0,
                    height: "100%",
                    width: "40px",
                    pointerEvents: "none", // Allow scrollbar interaction
                    zIndex: 1,
                  }}
                />
              )}

              {/* Custom vertical scrollbar - matches OverlayScrollbar styling */}
              {hasVerticalOverflow && (
                <CustomScrollbarTrack
                  orientation="vertical"
                  visible={verticalScrollbarVisible}
                  onClick={handleVerticalTrackClick}
                  style={{
                    top: `${_configs.headerMinWidth +
                      _configs.labelsToGridGap +
                      xAxisLabelPadding
                      }px`,
                  }}
                >
                  <CustomScrollbarThumb
                    orientation="vertical"
                    isDragging={isDraggingVertical}
                    style={{
                      height: `${verticalScrollState.thumbHeight}px`,
                      top: `${verticalScrollState.thumbTop}px`,
                    }}
                    onMouseDown={handleVerticalThumbMouseDown}
                  />
                </CustomScrollbarTrack>
              )}

              {/* Custom horizontal scrollbar - matches OverlayScrollbar styling */}
              {hasHorizontalOverflow && (
                <CustomScrollbarTrack
                  orientation="horizontal"
                  visible={horizontalScrollbarVisible}
                  onClick={handleHorizontalTrackClick}
                  style={{
                    left: `${yWidth + _configs.labelsToGridGap + yAxisLabelPadding
                      }px`,
                  }}
                >
                  <CustomScrollbarThumb
                    orientation="horizontal"
                    isDragging={isDraggingHorizontal}
                    style={{
                      width: `${horizontalScrollState.thumbWidth}px`,
                      left: `${horizontalScrollState.thumbLeft}px`,
                    }}
                    onMouseDown={handleHorizontalThumbMouseDown}
                  />
                </CustomScrollbarTrack>
              )}
            </StyledGridContainer>
          </div>
        </div>
      </VizArea>
      <SidePanel
        isOpen={!!selectedCell}
        onClose={() => setSelectedKey(null)}
        title={selectedCell && `${selectedCell.name} \u00D7 ${selectedCell.tooltipLabel}`}
        subHeader={selectedCell && renderCellSubHeader?.(selectedCell)}
        // Clicking cell to cell would otherwise close then reopen the panel.
        closeOnOutsideClick={false}
        exclusiveGroup="datumDetail"
        contentKey={selectedKey}
      >
        {selectedCell && renderCellDetails(selectedCell)}
      </SidePanel>
    </VizShell>
  );
};
